import {
  ColorDto,
  FontDto,
  LogoOptionsDto,
  TransferMethodDto,
} from "../responses/logoOptionsDto";
import {
  Color,
  Font,
  LogoOptions,
  OversizeSheetGuardHint,
  TransferMethod,
} from "../../../core/models/entities/LogoOptions";
import { OversizeSheetGuardDto } from "../responses/logoOptionsDto";

// The inactive hint: an API without the field (flag off, or older API) must
// never activate the guard. maxMm mirrors the API default so a partial
// object still judges 300 vs 301 the same way as the server.
export const INACTIVE_OVERSIZE_SHEET_GUARD: OversizeSheetGuardHint = {
  enabled: false,
  maxMm: 300,
  applies: false,
  requestEnabled: false,
  requestTtlHours: 24,
  vendorIds: [],
  bothSides: false,
  designsExempt: false,
};

export function mapOversizeSheetGuard(
  dto: OversizeSheetGuardDto | null | undefined,
): OversizeSheetGuardHint {
  if (!dto || typeof dto !== "object") return INACTIVE_OVERSIZE_SHEET_GUARD;
  const maxMm = Number(dto.maxMm);
  const ttl = Number(dto.requestTtlHours);
  return {
    enabled: dto.enabled === true,
    maxMm: Number.isFinite(maxMm) && maxMm > 0 ? maxMm : 300,
    applies: dto.applies === true,
    requestEnabled: dto.requestEnabled === true,
    requestTtlHours: Number.isFinite(ttl) && ttl > 0 ? ttl : 24,
    vendorIds: Array.isArray(dto.vendorIds)
      ? dto.vendorIds.filter((id) => typeof id === "number" && id > 0)
      : [],
    bothSides: dto.bothSides === true,
    designsExempt: dto.designsExempt === true,
  };
}

export function mapLogoOptions(dto: LogoOptionsDto): LogoOptions {
  // Null-guards on every catalog array: a partial/degraded payload must not
  // throw inside the query transform — that would flip the consuming screen
  // (design/logo editor panels) to the error view even though the server
  // responded 200.
  return {
    ...dto,
    transferMethods: dto.transferMethods
      ? dto.transferMethods.map(mapToTransferMethod)
      : [],
    transferMethodsNumbers: dto.transferMethodsNumbers
      ? dto.transferMethodsNumbers.map(mapToTransferMethod)
      : [],
    transferMethodsNames: dto.transferMethodsNames
      ? dto.transferMethodsNames.map(mapToTransferMethod)
      : [],
    colors: dto.colors ? dto.colors.map(mapToColor) : [],
    allColors: dto.allColors ? dto.allColors.map(mapToColor) : [],
    colorsEmbroidery: dto.colorsEmbroidery
      ? dto.colorsEmbroidery.map(mapToColor)
      : [],
    transferLogoTypes: dto.logoTypes ?? [],
    fonts: dto.fonts ? dto.fonts.map(mapToFont) : [],
    fontSizes: dto.fontSizes ? dto.fontSizes.map(mapToFontSize) : [],
    fontSizesNumbers: dto.fontSizesNumbers
      ? dto.fontSizesNumbers.map(mapToFontSize)
      : [],
    fontSizesNames: dto.fontSizesNames
      ? dto.fontSizesNames.map(mapToFontSize)
      : [],
    blockedMethodKeys: dto.blockedMethodKeys ?? [],
    // Fail-open: an API that doesn't send the flag yet behaves as "enabled".
    stockOrdersDisabled: dto.stockOrdersDisabled ?? false,
    // Per-user effective block; an API that doesn't send it yet (pre
    // per-customer-blocking deploy) falls back to the global flag.
    stockOrdersEffectiveDisabled:
      dto.stockOrdersEffectiveDisabled ?? dto.stockOrdersDisabled ?? false,
    // Oversize-sheet guard hint; absent (API flag off / older API) = inactive.
    oversizeSheetGuard: mapOversizeSheetGuard(dto.oversizeSheetGuard),
  };
}

function mapToColor(dto: ColorDto): Color {
  return dto;
}

function mapToTransferMethod(dto: TransferMethodDto): TransferMethod {
  return dto;
}

function mapToFont(dto: FontDto): Font {
  return dto;
}

function mapToFontSize(dto: number): number {
  return dto;
}
